/* eslint-disable jsx-a11y/anchor-is-valid */
import { ArrowDropDown, MoreHorizRounded, WarningRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Dropdown,
  FormControl,
  FormLabel,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  Modal,
  ModalDialog,
  Option,
  Select,
  Typography,
  useTheme,
} from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useState } from 'react';

import { InvitationStatus, ProjectId, User, UserId, UserProjectRole } from '@builder-bud/common';
import {
  AlertObject,
  USER_PROJECT_ROLE_OPTIONS,
  getUserProjectRole,
  getUserProjectRoleLabel,
  showErrorAlert,
  showInfoAlert,
  useAppDispatch,
  useDeleteProjectUserMutation,
  useGetMeInvitationsQuery,
  useGetUsersQuery,
} from '@builder-bud/common-ui';

import CircularProgressComponent from '../../components/circular-progress.component';
import MemberComponent from '../../components/member.component';
import { Order, getComparator } from '../../components/table';
import TableComponent from '../../components/table.component';

export default function MembersTableComponent({ rows = [], projectId }: { rows?: UserId[]; projectId?: ProjectId }) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const smallAndUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [deleteProjectUser] = useDeleteProjectUserMutation();

  const [order, setOrder] = useState<Order>('');
  const [projectRoleFilter, setProjectRoleFilter] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [rowToRemove, setRowToRemove] = useState<User | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const { data: invitations } = useGetMeInvitationsQuery();
  const { data: users = [] } = useGetUsersQuery();
  const filteredRows = users.flatMap((user: User) => {
    if (rows.includes(user.id)) {
      const userWithProjectRole = {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phone: user.phone,
        projectRole: getUserProjectRole(projectId, user?.projects),
      };

      if (!projectRoleFilter.length || projectRoleFilter.includes(userWithProjectRole.projectRole as UserProjectRole)) {
        return [userWithProjectRole];
      }
    }
    return [];
  });

  async function handleRemoveMember() {
    if (rowToRemove) {
      setIsRemoving(true);
      try {
        await deleteProjectUser({ id: projectId!, userId: rowToRemove.id }).unwrap();
        dispatch(showInfoAlert('Successfully removed Member'));
      } catch (error) {
        console.log(error);
        dispatch(showErrorAlert(error as AlertObject));
      } finally {
        setModalOpen(false);
        setRowToRemove(null);
        setIsRemoving(false);
      }
    }
  }

  const renderFilters = () => (
    <FormControl size="sm">
      <FormLabel>Project role</FormLabel>
      <Select
        value={projectRoleFilter}
        onChange={(e, newValue) => setProjectRoleFilter(newValue)}
        size="sm"
        placeholder="Filter by project role"
        slotProps={{ button: { sx: { whiteSpace: 'nowrap' } } }}
        multiple={true}
      >
        {USER_PROJECT_ROLE_OPTIONS.map((option, i) => (
          <Option key={option.value} value={option.value}>
            {option.label}
          </Option>
        ))}
      </Select>
    </FormControl>
  );

  const renderTableHead = () => (
    <thead>
      <tr>
        <th style={{ padding: '12px 6px' }}>
          <Link
            underline="none"
            color="primary"
            component="button"
            onClick={() => setOrder(order === '' ? 'asc' : order === 'asc' ? 'desc' : '')}
            fontWeight="lg"
            endDecorator={<ArrowDropDown />}
            sx={{
              '& svg': {
                transition: '0.2s',
                transform: order === '' ? 'rotate(90deg)' : order === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)',
              },
            }}
          >
            Member
          </Link>
        </th>
        {smallAndUp && (
          <>
            <th style={{ padding: '12px 6px' }}>Email</th>
            <th style={{ padding: '12px 6px' }}>Phone</th>
          </>
        )}
        <th style={{ padding: '12px 6px' }}>Role</th>
        <th style={{ width: 48, padding: '12px 6px' }}></th>
      </tr>
    </thead>
  );

  function TableRowMenu({ row }: { row: User }) {
    const isAllowedRemoveUserFromProject = invitations?.sent.some(
      (i) => i.projectId === projectId && i.email === row.email && i.status === InvitationStatus.Accepted
    );

    return isAllowedRemoveUserFromProject ? (
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm' } }}
        >
          <MoreHorizRounded />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          <Divider />
          <MenuItem
            color="danger"
            onClick={() => {
              setRowToRemove(row);
              setModalOpen(true);
            }}
          >
            Remove
          </MenuItem>
        </Menu>
      </Dropdown>
    ) : null;
  }

  function TableRow({ row }: { row: User & { projectRole: UserProjectRole } }) {
    return (
      <tr key={row.id} style={{ backgroundColor: 'var(--joy-palette-common-white)' }}>
        <td>
          <MemberComponent userId={row.id} showName={smallAndUp} />
        </td>
        {smallAndUp && (
          <>
            <td>
              <Typography level="body-xs">{row.email}</Typography>
            </td>
            <td>
              <Typography level="body-xs">{row.phone}</Typography>
            </td>
          </>
        )}
        <td>
          <Typography>{getUserProjectRoleLabel(row.projectRole)}</Typography>
        </td>
        <td>
          <TableRowMenu row={row} />
        </td>
      </tr>
    );
  }

  if (isRemoving) return <CircularProgressComponent />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TableComponent
        renderFilters={renderFilters}
        renderTableHead={renderTableHead}
        TableRow={TableRow}
        rows={filteredRows}
        comparator={getComparator(order, 'lastName')}
      />
      <Modal
        open={modalOpen}
        onClose={() => {
          //setRowToRemove(null);
          setModalOpen(false);
        }}
      >
        <ModalDialog variant="outlined" role="alertdialog">
          <DialogTitle>
            <WarningRounded />
            Confirmation
          </DialogTitle>
          <Divider />
          <DialogContent>Are you sure you want to remove this member?</DialogContent>
          <DialogActions>
            <Button variant="solid" color="danger" onClick={() => handleRemoveMember()}>
              Remove member
            </Button>
            <Button variant="plain" color="neutral" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
